import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import qrcode from "qrcode-generator";
import { barcodeSvg } from "@/lib/code128";
import { money } from "@/lib/format";
import { useTill } from "@/lib/till-context";
import type { Item } from "@/lib/types";
import { Button, Input, Modal, cx } from "./ui";

/**
 * Barcode labels. Two ways to print:
 *  - A4 sheet, landscape: labels in rows across the page with crop marks in the margin and
 *    dashed cut lines between labels (any office printer, cut by hand or guillotine).
 *  - Label printer roll: one label per page at the label's own size.
 * Everything is laid out in millimetres so it prints at true size (printer at 100%).
 */
type Size = { key: string; w: number; h: number; note: string };
const SIZES: Size[] = [
  { key: "54x30", w: 54, h: 30, note: "Shelf & box labels" },
  { key: "38x21", w: 38, h: 21, note: "Small accessories" },
  { key: "63x38", w: 63.5, h: 38, note: "Big boxes, TVs" },
  { key: "100x70", w: 100, h: 70, note: "Shelves and yard bays, big price" },
];
const PAGE = { w: 297, h: 210, margin: 10 };
const grid = (s: Size) => {
  const cols = Math.floor((PAGE.w - PAGE.margin * 2) / s.w);
  const rows = Math.floor((PAGE.h - PAGE.margin * 2) / s.h);
  return { cols, rows, per: cols * rows, left: (PAGE.w - cols * s.w) / 2, top: (PAGE.h - rows * s.h) / 2 };
};

type Opts = { size: Size; cutMarks: boolean; price: boolean; sku: boolean; qr: boolean };

/** A QR code as SVG markup that fills its box (2-module quiet zone). Phones and 2D scanners read it. */
function qrSvg(text: string) {
  const q = qrcode(0, "M");
  q.addData(text);
  q.make();
  const n = q.getModuleCount();
  let d = "";
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (q.isDark(y, x)) d += `M${x + 2} ${y + 2}h1v1h-1z`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n + 4} ${n + 4}" width="100%" height="100%" shape-rendering="crispEdges"><rect width="${n + 4}" height="${n + 4}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
}

function Label({ item, o, roll }: { item: Item; o: Opts; roll?: boolean }) {
  const code = item.barcode ?? item.sku ?? "";
  const small = o.size.h < 25;
  const big = o.size.h >= 60;
  if (o.qr) {
    const pad = small ? 1.2 : 2;
    return (
      <div style={{ width: `${o.size.w}mm`, height: `${o.size.h}mm`, boxSizing: "border-box", padding: `${pad}mm`, display: "flex", gap: "1.6mm", fontFamily: "Arial, Helvetica, sans-serif", color: "#000", background: "#fff", overflow: "hidden", ...(roll ? {} : { borderRight: o.cutMarks ? "0.2mm dashed #9ca3af" : "none", borderBottom: o.cutMarks ? "0.2mm dashed #9ca3af" : "none" }) }}>
        <div style={{ width: `${big ? 42 : o.size.h - pad * 2}mm`, height: `${big ? 42 : o.size.h - pad * 2}mm`, flex: "none", alignSelf: "center" }} dangerouslySetInnerHTML={{ __html: qrSvg(code) }} />
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0, flex: 1 }}>
          <b style={{ fontSize: big ? "15pt" : small ? "6.5pt" : "7.5pt", lineHeight: 1.15, maxHeight: big ? "30mm" : small ? "7.5mm" : "13mm", overflow: "hidden" }}>{item.name}</b>
          {o.price ? <b style={{ fontSize: big ? "24pt" : small ? "8pt" : "11pt" }}>{money(item.price)}{item.unit ? <span style={{ fontSize: big ? "10pt" : small ? "5.5pt" : "6.5pt", fontWeight: 400 }}> / {item.unit}</span> : null}</b> : null}
          <span style={{ fontFamily: "'Courier New', monospace", fontSize: big ? "9pt" : small ? "5pt" : "6pt" }}>{code}</span>
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        width: `${o.size.w}mm`,
        height: `${o.size.h}mm`,
        boxSizing: "border-box",
        padding: small ? "1.2mm 1.6mm" : "2mm 2.6mm",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#000",
        background: "#fff",
        overflow: "hidden",
        ...(roll ? {} : { borderRight: o.cutMarks ? "0.2mm dashed #9ca3af" : "none", borderBottom: o.cutMarks ? "0.2mm dashed #9ca3af" : "none" }),
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1.5mm" }}>
        <b style={{ fontSize: big ? "15pt" : small ? "6.5pt" : "7.5pt", lineHeight: 1.15, maxHeight: big ? "20mm" : small ? "5mm" : "6.6mm", overflow: "hidden" }}>{item.name}</b>
        {o.price ? <b style={{ fontSize: big ? "24pt" : small ? "7pt" : "8.5pt", whiteSpace: "nowrap" }}>{money(item.price)}{big && item.unit ? <span style={{ fontSize: "10pt", fontWeight: 400 }}> / {item.unit}</span> : null}</b> : null}
      </div>
      <div style={{ height: small ? "8mm" : big ? "26mm" : o.size.h >= 35 ? "15mm" : "11mm", display: "flex", justifyContent: "center" }} dangerouslySetInnerHTML={{ __html: barcodeSvg(code, 40).replace(`height="40"`, `height="100%"`) }} />
      <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "'Courier New', monospace", fontSize: small ? "5.5pt" : "6.5pt" }}>
        <span>{code}</span>
        {o.sku && item.sku && item.sku !== code ? <span>{item.sku}</span> : null}
      </div>
    </div>
  );
}

/** One A4 landscape page: crop marks in the margin, labels in a grid. `cells` may hold nulls (skipped labels). */
function Sheet({ cells, o, store, page, pages }: { cells: (Item | null)[]; o: Opts; store: string; page: number; pages: number }) {
  const g = grid(o.size);
  const mark = { position: "absolute" as const, background: "#000" };
  return (
    <div style={{ width: `${PAGE.w}mm`, height: `${PAGE.h}mm`, position: "relative", background: "#fff", overflow: "hidden", boxSizing: "border-box", pageBreakAfter: "always", breakAfter: "page" }}>
      <div style={{ position: "absolute", left: `${g.left}mm`, right: `${g.left}mm`, top: "2.5mm", display: "flex", justifyContent: "space-between", fontFamily: "Arial, sans-serif", fontSize: "6.5pt", color: "#6b7280" }}>
        <span>
          {store} · barcode labels · {o.size.w} × {o.size.h} mm · {g.per} per sheet
        </span>
        <span>{o.cutMarks ? "✂ Cut along the dashed lines · " : ""}sheet {page} of {pages}</span>
      </div>
      {o.cutMarks ? (
        <>
          {Array.from({ length: g.cols + 1 }, (_, i) => (
            <span key={`c${i}`}>
              <span style={{ ...mark, left: `${g.left + i * o.size.w}mm`, top: `${g.top - 6}mm`, width: "0.2mm", height: "4mm" }} />
              <span style={{ ...mark, left: `${g.left + i * o.size.w}mm`, top: `${g.top + g.rows * o.size.h + 2}mm`, width: "0.2mm", height: "4mm" }} />
            </span>
          ))}
          {Array.from({ length: g.rows + 1 }, (_, i) => (
            <span key={`r${i}`}>
              <span style={{ ...mark, top: `${g.top + i * o.size.h}mm`, left: `${g.left - 6}mm`, height: "0.2mm", width: "4mm" }} />
              <span style={{ ...mark, top: `${g.top + i * o.size.h}mm`, left: `${g.left + g.cols * o.size.w + 2}mm`, height: "0.2mm", width: "4mm" }} />
            </span>
          ))}
        </>
      ) : null}
      <div style={{ position: "absolute", left: `${g.left}mm`, top: `${g.top}mm`, display: "grid", gridTemplateColumns: `repeat(${g.cols}, ${o.size.w}mm)`, gridAutoRows: `${o.size.h}mm`, outline: o.cutMarks ? "0.2mm dashed #9ca3af" : "none" }}>
        {Array.from({ length: g.per }, (_, i) => {
          const it = cells[i];
          return it ? <Label key={i} item={it} o={o} /> : <div key={i} style={{ borderRight: o.cutMarks ? "0.2mm dashed #9ca3af" : "none", borderBottom: o.cutMarks ? "0.2mm dashed #9ca3af" : "none" }} />;
        })}
      </div>
    </div>
  );
}

export function LabelsModal({ items, onClose }: { items: Item[]; onClose: () => void }) {
  const { store } = useTill();
  const [mode, setMode] = useState<"sheet" | "roll">("sheet");
  const [size, setSize] = useState<Size>(SIZES[0]!);
  const [start, setStart] = useState(1);
  const [cutMarks, setCutMarks] = useState(true);
  const [price, setPrice] = useState(true);
  const [sku, setSku] = useState(true);
  const [qr, setQr] = useState(false);
  const [copies, setCopies] = useState<Record<string, number>>(() => Object.fromEntries(items.map((i) => [i.id, 1])));
  const o: Opts = { size, cutMarks: mode === "sheet" && cutMarks, price, sku, qr };
  const g = grid(size);
  const all = useMemo(() => items.flatMap((i) => Array.from({ length: copies[i.id] ?? 0 }, () => i)), [items, copies]);
  const sheets = useMemo(() => {
    const cells: (Item | null)[] = [...Array.from({ length: Math.min(g.per - 1, Math.max(0, start - 1)) }, () => null), ...all];
    const out: (Item | null)[][] = [];
    for (let i = 0; i < cells.length; i += g.per) out.push(cells.slice(i, i + g.per));
    return out.length ? out : [[]];
  }, [all, start, g.per]);
  const storeName = store?.storeName ?? "Store";

  const fill = () => {
    const free = g.per - (Math.max(1, start) - 1);
    const n = items.length || 1;
    const each = Math.max(1, Math.floor(free / n));
    const next: Record<string, number> = {};
    items.forEach((i, idx) => (next[i.id] = each + (idx < free - each * n ? 1 : 0)));
    setCopies(next);
  };

  // The preview scales to whatever width its column has (no overflow onto the settings).
  const box = useRef<HTMLDivElement>(null);
  const [boxW, setBoxW] = useState(600);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setBoxW(e!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pageCss = mode === "sheet" ? `@page { size: A4 landscape; margin: 0; }` : `@page { size: ${size.w}mm ${size.h}mm; margin: 0; }`;
  const preview = Math.min(1, Math.max(0.2, (boxW - 8) / ((PAGE.w * 96) / 25.4)));

  return (
    <Modal
      xl
      title="Print labels"
      sub={`${items.length} product${items.length === 1 ? "" : "s"} · ${all.length} label${all.length === 1 ? "" : "s"}${mode === "sheet" ? ` · ${sheets.length} sheet${sheets.length === 1 ? "" : "s"}` : ""}`}
      onClose={onClose}
      footer={
        <>
          {mode === "sheet" ? <Button onClick={fill}>Fill the sheet</Button> : null}
          <Button tone="primary" onClick={() => window.print()} disabled={!all.length}>
            Print {all.length} label{all.length === 1 ? "" : "s"}
          </Button>
        </>
      }
    >
      <div className="grid min-w-0 gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="grid grid-cols-2 border border-slate-200">
            {(["sheet", "roll"] as const).map((m) => (
              <button key={m} type="button" onClick={() => setMode(m)} className={cx("h-11 text-[13px] font-semibold", mode === m ? "bg-slate-900 text-white" : "bg-white")}>
                {m === "sheet" ? "A4 sheet, landscape" : "Label printer roll"}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold">Code</span>
            <div className="grid grid-cols-2 border border-slate-200">
              {([[false, "Barcode"], [true, "QR code"]] as const).map(([v, l]) => (
                <button key={l} type="button" onClick={() => setQr(v)} className={cx("h-10 text-[13px] font-semibold", qr === v ? "bg-slate-900 text-white" : "bg-white")}>
                  {l}
                </button>
              ))}
            </div>
            <span className="text-[11px] text-slate-500">{qr ? "Read by phone cameras and 2D scanners. Older red-line scanners can't read QR." : "Read by every scanner, including older red-line ones."}</span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-[13px] font-semibold">Label size</span>
            {SIZES.map((s) => {
              const gg = grid(s);
              return (
                <label key={s.key} className={cx("flex cursor-pointer items-center gap-2.5 border px-3 py-2.5 text-[13px]", size.key === s.key ? "border-brand bg-brand-tint" : "border-slate-200")}>
                  <input type="radio" name="label-size" checked={size.key === s.key} onChange={() => setSize(s)} className="h-4 w-4 accent-[var(--brand)]" />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span>
                      <b>
                        {s.w} × {s.h} mm
                      </b>
                      {mode === "sheet" ? ` · ${gg.per} per sheet` : ""}
                    </span>
                    <span className="text-[11px] text-slate-500">{s.note}</span>
                  </span>
                </label>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {mode === "sheet" ? (
              <label className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold">Start at label</span>
                <Input inputMode="numeric" value={String(start)} onChange={(e) => setStart(Math.min(g.per, Math.max(1, Number(e.target.value.replace(/\D/g, "")) || 1)))} className="font-mono" />
                <span className="text-[11px] text-slate-500">Skip labels already used</span>
              </label>
            ) : (
              <span />
            )}
            <div className="flex flex-col gap-2 pt-1 text-[13px]">
              {mode === "sheet" ? (
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={cutMarks} onChange={(e) => setCutMarks(e.target.checked)} className="h-4 w-4 accent-[var(--brand)]" /> Cut marks
                </label>
              ) : null}
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={price} onChange={(e) => setPrice(e.target.checked)} className="h-4 w-4 accent-[var(--brand)]" /> Show price
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={sku} onChange={(e) => setSku(e.target.checked)} className="h-4 w-4 accent-[var(--brand)]" /> Show SKU
              </label>
            </div>
          </div>
          <div className="flex flex-col">
            <span className="mb-1 text-[13px] font-semibold">Copies</span>
            {!items.length ? <p className="text-sm text-slate-500">None of the products shown have a barcode or SKU yet.</p> : null}
            {items.map((i) => (
              <div key={i.id} className="flex items-center gap-2 border-b border-slate-100 py-1.5 text-[13px]">
                <span className="min-w-0 flex-1 truncate">{i.name}</span>
                <div className="flex items-center border border-slate-200">
                  <button type="button" aria-label="Fewer" className="h-8 w-8" onClick={() => setCopies({ ...copies, [i.id]: Math.max(0, (copies[i.id] ?? 0) - 1) })}>
                    −
                  </button>
                  <span className="w-8 text-center font-mono font-bold">{copies[i.id] ?? 0}</span>
                  <button type="button" aria-label="More" className="h-8 w-8" onClick={() => setCopies({ ...copies, [i.id]: Math.min(500, (copies[i.id] ?? 0) + 1) })}>
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div ref={box} className="flex min-w-0 flex-col items-center gap-2 self-start bg-slate-200 p-4 lg:sticky lg:top-0">
          {mode === "sheet" ? (
            <div className="overflow-hidden shadow-lg" style={{ width: `calc(${PAGE.w}mm * ${preview})`, height: `calc(${PAGE.h}mm * ${preview})` }}>
              <div style={{ transform: `scale(${preview})`, transformOrigin: "top left" }}>
                <Sheet cells={sheets[0]!} o={o} store={storeName} page={1} pages={sheets.length} />
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-3">
              {all.slice(0, 6).map((it, n) => (
                <div key={n} className="shadow">
                  <Label item={it} o={o} roll />
                </div>
              ))}
            </div>
          )}
          <span className="text-center text-xs text-slate-600">{mode === "sheet" ? "Preview of sheet 1. Print on A4, landscape, at 100% scale (turn off fit to page)." : "One label per page. Set the label printer's paper to the label size."}</span>
        </div>
      </div>

      {createPortal(
        <div className="print-only" style={{ position: "absolute", left: 0, top: 0, width: "auto", padding: 0, fontFamily: "Arial, sans-serif", fontSize: "inherit" }}>
          <style>{pageCss}</style>
          {mode === "sheet"
            ? sheets.map((cells, i) => <Sheet key={i} cells={cells} o={o} store={storeName} page={i + 1} pages={sheets.length} />)
            : all.map((it, n) => (
                <div key={n} style={{ pageBreakAfter: "always", breakAfter: "page" }}>
                  <Label item={it} o={o} roll />
                </div>
              ))}
        </div>,
        document.body,
      )}
    </Modal>
  );
}
